// src/components/ui/IngresoAlmacenForm.jsx
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "./button";
import { Card, CardContent } from "./card";
import { toast } from "react-toastify";

import InputField from "./InputField";
import SelectField from "./SelectField";
import TextAreaField from "./TextAreaField";

const IngresoAlmacenForm = ({ onClose, onSave }) => {
  const [form, setForm] = useState({
    ordenId: "",
    proveedorId: "",
    fecha_ingreso: new Date().toISOString().split("T")[0],
    usuarioId: "",
    observaciones: "",
  });

  const [ordenes, setOrdenes] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("token");
  const API = process.env.REACT_APP_API_URL;

  /* ---------------------------------------------------------
     Cargar órdenes pendientes
  --------------------------------------------------------- */
  const fetchOrdenes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/ordenes/pendientes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrdenes(data);
    } catch {
      toast.error("No se pudieron cargar las órdenes de compra");
    }
  }, [API, token]);

  /* ---------------------------------------------------------
     Cargar ítems + stock
  --------------------------------------------------------- */
  const fetchItems = useCallback(
    async (ordenId) => {
      if (!ordenId) return;

      try {
        const { data } = await axios.get(`${API}/detalle/${ordenId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const codigos = data.map((i) => i._id || i.codigo);
        const stockRes = await axios.post(
          `${API}/stock/stock-consulta`,
          { codigos },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        const stockMap = stockRes.data || {};

        setItems(
          data.map((item) => ({
            ...item,
            codigo_item: item._id || item.codigo,
            articulo: item.descripcion || item.producto,
            cantidad_orden: item.cantidad || 0,
            cantidad: 0,
            precio_unitario: item.costo_unitario || 0,
            stock_actual: stockMap[item._id] || 0,
          })),
        );

        const orden = ordenes.find((o) => o._id === ordenId);
        if (orden) {
          setForm((s) => ({
            ...s,
            proveedorId: orden.proveedor?._id || "",
          }));
        }
      } catch {
        toast.error("No se pudieron cargar los ítems o el stock");
      }
    },
    [API, token, ordenes],
  );

  useEffect(() => {
    fetchOrdenes();
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (user?._id) setForm((s) => ({ ...s, usuarioId: user._id }));
  }, [fetchOrdenes]);

  /* ---------------------------------------------------------
     Handler único (CLAVE)
  --------------------------------------------------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((s) => ({ ...s, [name]: value }));

    if (name === "ordenId") {
      fetchItems(value);
    }
  };

  const handleCantidadChange = (index, value) => {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== index) return it;
        const cantidad = Number(value);
        if (cantidad > it.cantidad_orden) {
          toast.warning(
            "❗ La cantidad ingresada no puede superar la ordenada",
          );
          return it;
        }
        return { ...it, cantidad };
      }),
    );
  };

  /* ---------------------------------------------------------
     Submit
  --------------------------------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.ordenId) {
      toast.error("Debe seleccionar una orden de compra");
      return;
    }

    const itemsValidos = items.filter((i) => i.cantidad > 0);
    if (!itemsValidos.length) {
      toast.warning("Debe ingresar al menos una cantidad");
      return;
    }

    const ordenSeleccionada = ordenes.find((o) => o._id === form.ordenId);

    setLoading(true);
    try {
      const payload = {
        ordenId: form.ordenId,
        proveedorId: ordenSeleccionada?.proveedor?._id,
        usuarioId: form.usuarioId,
        observaciones: form.observaciones,
        tipo_movimiento: "Ingreso",
        items: itemsValidos.map((it) => ({
          codigo_item: it._id,
          articulo: it.producto,
          cantidad: it.cantidad,
          precio_unitario: it.costo_unitario,
          cantidad_orden: it.cantidad_orden,
        })),
      };

      const res = await axios.post(`${API}/stock/stock-multiple`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(res.data.message || "Ingreso de almacén registrado");
      if (onSave) onSave(payload);
      onClose();
    } catch (error) {
      const mensaje =
        error.response?.data?.message ||
        error.message ||
        "Error al registrar ingreso";

      toast.error(mensaje);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------------------------
     Render
  --------------------------------------------------------- */
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <Card className="border-none shadow-none">
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                📥 Ingreso de almacén
              </h2>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <SelectField
                  label="Orden de Compra *"
                  name="ordenId"
                  value={form.ordenId}
                  onChange={handleChange}
                  options={ordenes}
                  placeholder="Seleccione orden"
                  displayFn={(o) => `${o.descripcion || "NA"}`}
                />

                <InputField
                  label="Proveedor"
                  value={
                    ordenes.find((o) => o._id === form.ordenId)?.proveedor
                      ?.name || ""
                  }
                  readOnly
                />

                <InputField
                  label="Fecha de Ingreso"
                  value={form.fecha_ingreso}
                  readOnly
                />
              </div>

              <TextAreaField
                label="Observaciones"
                name="observaciones"
                value={form.observaciones}
                onChange={handleChange}
              />

              {items.length > 0 && (
                <div className="overflow-x-auto border rounded-md">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-left">Código</th>
                        <th className="px-3 py-2 text-left">Descripción</th>
                        <th className="px-3 py-2 text-center">Stock</th>
                        <th className="px-3 py-2 text-center">Ordenado</th>
                        <th className="px-3 py-2 text-center">Recibido</th>
                        <th className="px-3 py-2 text-center">Precio</th>
                        <th className="px-3 py-2 text-center">Final</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-3 py-1">{it._id}</td>
                          <td className="px-3 py-1">
                            {it.descripcion || it.producto}
                          </td>
                          <td className="px-3 py-1 text-center">
                            {it.stock_actual}
                          </td>
                          <td className="px-3 py-1 text-center">
                            {it.cantidad_orden}
                          </td>
                          <td className="px-3 py-1 text-center">
                            <input
                              type="number"
                              min="0"
                              max={it.cantidad_orden}
                              value={it.cantidad}
                              onChange={(e) =>
                                handleCantidadChange(i, e.target.value)
                              }
                              className="w-20 border rounded p-1 text-center"
                            />
                          </td>
                          <td className="px-3 py-1 text-center">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={it.costo_unitario}
                              onChange={(e) =>
                                setItems((prev) =>
                                  prev.map((x, j) =>
                                    j === i
                                      ? {
                                          ...x,
                                          costo_unitario: Number(
                                            e.target.value,
                                          ),
                                        }
                                      : x,
                                  ),
                                )
                              }
                              className="w-24 border rounded p-1 text-center"
                            />
                          </td>
                          <td className="px-3 py-1 text-center font-semibold">
                            {Number(it.stock_actual) + Number(it.cantidad)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  onClick={onClose}
                  className="bg-red-500 hover:bg-red-600 text-white"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  disabled={loading}
                >
                  {loading ? "Guardando..." : "Registrar Ingreso + Stock"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default IngresoAlmacenForm;
